// components/NextActionRenderer/NextActionRenderer.tsx
import React from "react";
import { IRowNode } from "ag-grid-community";
import styles from "./NextActionRenderer.module.css";

/**
 * NextActionRenderer
 *
 * Renders the "Next action" pill in the Engagement Letters grid.
 * - Uses NextAction.label and NextAction.tone from the API/Python side
 * - Decides which callback to trigger based on the label text
 * - Ensures the underlying AG Grid row is selected when the pill is clicked
 */
interface Props {
  data: any;
  rowNode?: IRowNode;

  onSendClientApproval?: (row: any) => void;
  onSendForSignature?: (row: any) => void;
  goToApproval?: (row: any) => void;
  onUploadWetSignDocument?: (row: any) => void;
  onResumeDraft?: (row: any) => void;
}

const NextActionRenderer: React.FC<Props> = (p) => {
  const next = p.data?.NextAction ?? p.data?.nextAction;

  if (!next) {
    return (
      <div className={styles.wrap}>
        <button className={`${styles.pill} ${styles.pillMuted}`} disabled>
          No action
        </button>
      </div>
    );
  }

  const label = String(next.label || "").toLowerCase();

  let onClickHandler: (() => void) | undefined;

  // ENGAGEMENT_NEXT_ACTION_DICT mappings
  if (label.startsWith("send el for approval")) {
    onClickHandler = () => p.onSendClientApproval?.(p.data);
  } else if (label.startsWith("approve el")) {
    onClickHandler = () => p.goToApproval?.(p.data);
  } else if (label.startsWith("send for esign")) {
    onClickHandler = () => p.onSendForSignature?.(p.data);
  } else if (label.startsWith("upload wetsigned el")) {
    onClickHandler = () => p.onUploadWetSignDocument?.(p.data);
  } else if (label === "resume draft") {
    onClickHandler = () => p.onResumeDraft?.(p.data);
  }
  // SIGNATURE_NEXT_ACTION_DICT informational-only labels:
  // "awaiting signature", "completed", "check status", "check signature status"
  // fall through with no handler → disabled pill

  const toneClass =
    next.tone === "success"
      ? styles.pillSuccess
      : next.tone === "warning"
      ? styles.pillWarning
      : next.tone === "danger"
      ? styles.pillDanger
      : next.tone === "info"
      ? styles.pillInfo
      : styles.pillMuted;

  const isDisabled = !onClickHandler;

  const handleClick = () => {
    // Sync AG Grid selection when clicking the pill
    if (p.rowNode) {
      p.rowNode.setSelected(true, true);
    }
    onClickHandler?.();
  };

  return (
    <div className={styles.wrap}>
      <button
        className={`${styles.pill} ${toneClass}`}
        disabled={isDisabled}
        onClick={isDisabled ? undefined : handleClick}
      >
        {next.label}
      </button>
    </div>
  );
};

export default NextActionRenderer;
